import React, { useState, useEffect } from "react";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import CommonFormActions from "../../components/common/CommonFormActions";
import CommonModal from "../../components/common/CommonModal";
import MainLayout from "../../components/layout/MainLayout";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Leave.css";

function LeaveMaster() {
  const [loading, setLoading] = useState(true);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentLeave, setCurrentLeave] = useState(null);
  const [companyOwner, setCompanyOwner] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    short_name: "",
    days: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      const response = await api.get("/leave-types");

      if (response.data.success) {
        setLeaveTypes(response.data.data);
        setCompanyOwner(response.data.companyOwner);
      }
    } catch (error) {
      console.error("Failed to fetch leave types:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load leave types",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Capitalize first letter
    let formattedValue = value;
    if (name === "name" || name === "short_name") {
      formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Leave name is required";
    }

    if (!formData.short_name.trim()) {
      newErrors.short_name = "Short name is required";
    }

    if (!formData.days || formData.days < 0) {
      newErrors.days = "Valid days count is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddLeave = () => {
    setFormData({ name: "", short_name: "", days: "" });
    setErrors({});
    setShowAddModal(true);
  };

  const handleEditLeave = (leave) => {
    setCurrentLeave(leave);
    setFormData({
      name: leave.name,
      short_name: leave.short_name,
      days: leave.days,
    });
    setErrors({});
    setShowEditModal(true);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const response = await api.post("/leave-types", formData);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Leave type created successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        setShowAddModal(false);
        fetchLeaveTypes();
      }
    } catch (error) {
      console.error("Failed to create leave type:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to create leave type",
      });
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const response = await api.put(
        `/leave-types/${currentLeave.id}`,
        formData,
      );

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Leave type updated successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        setShowEditModal(false);
        fetchLeaveTypes();
      }
    } catch (error) {
      console.error("Failed to update leave type:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to update leave type",
      });
    }
  };

  const handleDelete = async (leaveId, leaveName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete "${leaveName}" leave type?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6557ff",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.delete(`/leave-types/${leaveId}`);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Leave type deleted successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        fetchLeaveTypes();
      }
    } catch (error) {
      console.error("Failed to delete leave type:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to delete leave type",
      });
    }
  };

  const canManageLeaves = () => {
    return companyOwner;
  };

  const activeLeaves = leaveTypes.filter((leave) => leave.is_active === 1).length;
  const totalConfiguredDays = leaveTypes.reduce(
    (sum, leave) => sum + Number(leave.days || 0),
    0,
  );

  if (loading) {
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </MainLayout>
    );
  }

  const leaveColumns = [
    {
      header: "Sr. No.",
      render: (_, index) => index + 1,
    },
    {
      header: "Title",
      render: (leave) => `${leave.name} (${leave.short_name})`,
    },
    {
      header: "No. Days",
      render: (leave) => <span>Total: {leave.days}</span>,
    },
    {
      header: "Active Status",
      render: (leave) => (
        <span
          className={`status-pill ${leave.is_active === 1 ? "active" : "inactive"}`}
        >
          {leave.is_active === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    ...(canManageLeaves()
      ? [
          {
            header: "Action",
            render: (leave) => (
              <CommonActionMenu
                ariaLabel={`Actions for leave ${leave.name}`}
                actions={[
                  {
                    label: "Edit",
                    icon: "fas fa-edit",
                    onClick: () => handleEditLeave(leave),
                  },
                  {
                    label: "Delete",
                    icon: "fas fa-trash",
                    variant: "danger",
                    onClick: () => handleDelete(leave.id, leave.name),
                  },
                ]}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <MainLayout>
      <div className="leave-management-container">
        <div className="leave-card">
          <div className="card-header-custom">
            <div className="header-empty">
              <span className="leave-guidance-label">Leave Setup</span>
              <h3>Leaves & Holidays</h3>
              <p className="leave-applications-subtitle">
                Configure leave types, annual day counts, and the active leave catalogue available to employees.
              </p>
            </div>
            {canManageLeaves() && (
              <button className="btn-add-leave" onClick={handleAddLeave}>
                + Add Leave
              </button>
            )}
          </div>

          <div className="leave-guidance-banner">
            <div>
              <span className="leave-guidance-label">Leave Catalog</span>
              <strong>
                {leaveTypes.length
                  ? `${leaveTypes.length} leave type${leaveTypes.length > 1 ? "s" : ""} configured`
                  : "No leave types configured yet"}
              </strong>
              <p>
                Keep this list aligned with your leave policy so balances, approvals, and attendance reports all stay consistent.
              </p>
            </div>
            <div className="leave-summary-chips">
              <span className="leave-summary-chip">Active {activeLeaves}</span>
              <span className="leave-summary-chip">Annual days {totalConfiguredDays}</span>
            </div>
          </div>

          <div className="card-body-custom">
            <CommonTable
              columns={leaveColumns}
              data={leaveTypes}
              tableClassName="leave-table-clean"
              wrapperClassName="leave-table-wrap"
              noDataClassName="text-center py-5 no-data"
              emptyMessage="No leave types found"
            />
          </div>
        </div>

        {/* Add Leave Modal */}
        {showAddModal && (
          <CommonModal
            onClose={() => setShowAddModal(false)}
            title="Add New Leave"
            overlayClassName="modal-backdrop-custom"
            contentClassName="modal-custom"
            headerClassName="modal-header-custom"
            bodyClassName="modal-body-custom"
            footerClassName="modal-footer-custom"
            closeButtonClassName="close-modal-btn"
            footer={
              <CommonFormActions
                className="modal-footer-custom"
                cancelLabel="Close"
                submitLabel="Submit"
                onCancel={() => setShowAddModal(false)}
                onSubmit={handleSubmitAdd}
                submitType="button"
              />
            }
          >
              <form onSubmit={handleSubmitAdd}>
                <div>
                  <div className="form-row-custom">
                    <label>
                      Leave Name<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter leave name"
                      />
                      {errors.name && (
                        <span className="error-msg">{errors.name}</span>
                      )}
                    </div>
                  </div>
                  <div className="form-row-custom">
                    <label>
                      Short Name<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        name="short_name"
                        value={formData.short_name}
                        onChange={handleInputChange}
                        placeholder="Enter short name"
                      />
                      {errors.short_name && (
                        <span className="error-msg">{errors.short_name}</span>
                      )}
                    </div>
                  </div>
                  <div className="form-row-custom">
                    <label>
                      Days<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="number"
                        name="days"
                        value={formData.days}
                        onChange={handleInputChange}
                        placeholder="Enter number of days"
                        min="0"
                      />
                      {errors.days && (
                        <span className="error-msg">{errors.days}</span>
                      )}
                    </div>
                  </div>
                </div>
              </form>
          </CommonModal>
        )}

        {/* Edit Leave Modal */}
        {showEditModal && (
          <CommonModal
            onClose={() => setShowEditModal(false)}
            title="Edit Leave"
            overlayClassName="modal-backdrop-custom"
            contentClassName="modal-custom"
            headerClassName="modal-header-custom"
            bodyClassName="modal-body-custom"
            footerClassName="modal-footer-custom"
            closeButtonClassName="close-modal-btn"
            footer={
              <CommonFormActions
                className="modal-footer-custom"
                cancelLabel="Close"
                submitLabel="Submit"
                onCancel={() => setShowEditModal(false)}
                onSubmit={handleSubmitEdit}
                submitType="button"
              />
            }
          >
              <form onSubmit={handleSubmitEdit}>
                <div>
                  <div className="form-row-custom">
                    <label>
                      Leave Name<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                      {errors.name && (
                        <span className="error-msg">{errors.name}</span>
                      )}
                    </div>
                  </div>
                  <div className="form-row-custom">
                    <label>
                      Short Name<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        name="short_name"
                        value={formData.short_name}
                        onChange={handleInputChange}
                      />
                      {errors.short_name && (
                        <span className="error-msg">{errors.short_name}</span>
                      )}
                    </div>
                  </div>
                  <div className="form-row-custom">
                    <label>
                      Days<span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="number"
                        name="days"
                        value={formData.days}
                        onChange={handleInputChange}
                        min="0"
                      />
                      {errors.days && (
                        <span className="error-msg">{errors.days}</span>
                      )}
                    </div>
                  </div>
                </div>
              </form>
          </CommonModal>
        )}
      </div>
    </MainLayout>
  );
}

export default LeaveMaster;
